export const SITE = {
  name: "AnxietyResource",
  domain: "anxietyresource.org",
  url: "https://anxietyresource.org",
  tagline: "clinical, plain-language anxiety education",
  description:
    "A clinically reviewed publication explaining what anxiety is, what it does to the body and mind, and what actually helps.",
  ogImage: "https://anxietyresource.org/brand/logo-wordmark.png",
  founded: "2026",
} as const;

// One-job framing, shared by the homepage and the network "next step" block so
// this property's role reads identically everywhere (network-wide convention).
export const SITE_ONE_JOB_LABEL = "anxiety-specific" as const;
export const NETWORK_PROMISE =
  "Each site in the network has one job. No matter where you enter, we help you find the next step that makes sense." as const;
// The universal network message. Placed verbatim on the homepage; kept here so
// it stays identical to every other Shrink Network property.
export const NETWORK_MASTER_MESSAGE =
  "The Shrink Network is an independent mental health knowledge ecosystem built around one idea: mental health is too important to squeeze into one website. Each site has one job. Together, they help people understand, apply, and access care. No matter where you enter the network, we help you find the next step that makes sense." as const;

// Canonical JSON-LD node IDs. The static Organization + WebSite nodes in
// index.html ship on every prerendered page, so builders reference these by
// @id to merge into a single connected entity graph (never duplicate nodes).
// Keep the trailing-slash form identical to index.html.
export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;

export const REVIEWER = {
  name: "Shariq Refai, MD, MBA",
  shortName: "Shariq Refai, MD, MBA",
  // Bare name + honorific suffix, used by the canonical Person JSON-LD node
  // (network-wide convention: name "Shariq Refai", honorificSuffix below).
  displayName: "Shariq Refai",
  honorific: "MD, MBA, FAPA",
  credential: "Board-certified Psychiatrist",
  // Public profile URL used everywhere "Shariq Refai" is rendered as a link.
  // Per editorial direction, name mentions point to his personal site.
  profileUrl: "https://shariqrefai.com",
  // Internal contributor route still exists for the in-site bio page and is
  // referenced by sitemap/breadcrumbs, but is no longer the link target for
  // bylines or in-body name mentions.
  internalPath: "/contributors/shariq-refai",
  // Persistent author identifiers used in Person JSON-LD (`identifier` +
  // `sameAs`). These are how the Google Knowledge Graph and other retrieval
  // systems resolve every Refai mention to the same real-world entity.
  orcid: "0009-0009-1090-4373",
  orcidUrl: "https://orcid.org/0009-0009-1090-4373",
  wikidataId: "Q139822307",
  wikidataUrl: "https://www.wikidata.org/wiki/Q139822307",
  // U.S. National Provider Identifier issued by CMS. The NPI Registry is a
  // .hhs.gov government source and is the highest-authority verification
  // channel for a U.S. physician identity.
  npi: "1467680660",
  npiUrl: "https://npiregistry.cms.hhs.gov/provider-view/1467680660",
  // Refai profile page on shrinkMD, part of the network-wide sameAs cluster.
  shrinkmdProfileUrl: "https://shrinkmd.com/shariq-refai",
} as const;

// Canonical network-wide JSON-LD node IDs. Every property in The Shrink
// Network emits the same Person @id and points its publisher chain at the
// same network Organization @id, so entity graphs merge across all sites.
export const PERSON_ID = "https://shariqrefai.com/#person" as const;
export const SHRINK_NETWORK_ORG_ID =
  "https://shrinknetwork.com/#organization" as const;

export const SHRINKMD_URL = "https://shrinkmd.com" as const;
/**
 * shrinkMD's anxiety-disorders service page. Used as the primary
 * "get care" CTA target from AR's homepage and category-level pages, per
 * the network inbound-link contract. Canonical form includes the trailing
 * slash, which matches shrinkMD's URL rewrites and avoids a redirect.
 */
export const CARE_ANXIETY_URL =
  "https://shrinkmd.com/anxiety-disorders" as const;
/**
 * shrinkMD's team profile for Shariq Refai, MD, MBA. Used as the reviewer
 * byline link on the About page.
 */
export const CARE_REFAI_URL =
  "https://shrinkmd.com/shariq-refai" as const;
/**
 * shrinkMD's direct start-care route. Used by the "When evaluation may
 * help" block on condition pages so readers who already know they want a
 * consult can skip the marketing homepage. The audit script checks that
 * this URL resolves 2xx on every build.
 */
export const CARE_START_URL =
  "https://shrinkmd.com/start-care" as const;
export const DEPRESSION_RESOURCE_URL = "https://depressionresource.org/" as const;
export const PSYCHIATRYRX_URL = "https://psychiatryrx.org" as const;
export const ANXIETYRESEARCH_URL = "https://anxietyresearch.org" as const;
export const SHRINKTIONARY_URL = "https://shrinktionary.com" as const;
export const SHRINKOPEDIA_URL = "https://shrinkopedia.com" as const;
export const SHRINQ_URL = "https://shrinq.com" as const;
export const BEUNSTUCK_URL = "https://beunstuck.app" as const;
export const QUITSCROLLING_URL = "https://quitscrolling.app" as const;
export const SHRINKIATRY_URL = "https://shrinkiatry.com" as const;
export const SHRINKDAILY_URL = "https://shrinkdaily.com" as const;
export const SHRINK_NETWORK_URL = "https://shrinknetwork.com" as const;

/**
 * Network-wide teal used for the "Part of The Shrink Network" tag in the
 * header and footer of every property in the network. Identical hex on
 * every property by design, so the network reads as a network.
 */
export const SHRINK_NETWORK_TEAL = "#58B8B1" as const;

/**
 * Sister properties in The Shrink Network, all medically edited or
 * created by the same reviewer. Rendered in the footer "Network" block.
 * ShrinkNetwork.com is the umbrella front door and is rendered
 * separately above this list.
 */
export const NETWORK_SITES = [
  {
    label: "Psychiatric care (telepsychiatry)",
    name: "shrinkMD.com",
    url: SHRINKMD_URL,
  },
  {
    label: "Depression",
    name: "DepressionResource.org",
    url: DEPRESSION_RESOURCE_URL.replace(/\/$/, ""),
  },
  {
    label: "Anxiety research and evidence",
    name: "AnxietyResearch.org",
    url: ANXIETYRESEARCH_URL,
  },
  {
    label: "Psychiatric medications",
    name: "PsychiatryRx.org",
    url: PSYCHIATRYRX_URL,
  },
  {
    label: "Structured learning program",
    name: "shrinQ.com",
    url: SHRINQ_URL,
  },
  {
    label: "Wellness companion",
    name: "Unstuck (beunstuck.app)",
    url: BEUNSTUCK_URL,
  },
  {
    label: "Mental health glossary",
    name: "Shrinktionary.com",
    url: SHRINKTIONARY_URL,
  },
  {
    label: "Mental health knowledge graph",
    name: "Shrinkopedia.com",
    url: SHRINKOPEDIA_URL,
  },
  {
    label: "Daily mental health concepts",
    name: "ShrinkDaily.com",
    url: SHRINKDAILY_URL,
  },
  {
    label: "Founder and medical editor",
    name: "ShariqRefai.com",
    url: "https://shariqrefai.com",
  },
] as const;

/**
 * Hostnames (without `www.`) of the other properties in The Shrink Network.
 * Cross-network links get the UTM parameters via `networkUrl()`; the
 * site's own domain is deliberately excluded so self-links stay clean.
 */
export const NETWORK_HOSTS: readonly string[] = [
  "shrinkmd.com",
  "depressionresource.org",
  "psychiatryrx.org",
  "anxietyresearch.org",
  "shrinktionary.com",
  "shrinkopedia.com",
  "shrinkdaily.com",
  "shrinq.com",
  "beunstuck.app",
  "quitscrolling.app",
  "shrinkiatry.com",
  "shrinknetwork.com",
  "shariqrefai.com",
] as const;

/**
 * Appends the cross-network UTM parameters to a cross-network URL:
 * utm_source=anxietyresource, utm_medium=network_journey,
 * utm_campaign=<topic>. Non-network and relative URLs pass through
 * unchanged, so this is safe to apply to any href.
 */
export function networkUrl(url: string, campaign: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (!NETWORK_HOSTS.includes(host)) return url;
    u.searchParams.set("utm_source", "anxietyresource");
    u.searchParams.set("utm_medium", "network_journey");
    // Preserve an author-specified utm_campaign so per-link topic-slug
    // campaigns written into MDX bodies (or component call sites) survive
    // the rehype rewrite. Only apply the default when the URL carries no
    // campaign of its own. This is what lets reciprocal deep-link entries
    // from Shrinkopedia's network-crosslink-map keep their exact topic
    // campaigns end-to-end.
    if (!u.searchParams.get("utm_campaign")) {
      u.searchParams.set("utm_campaign", campaign);
    }
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * The site's final URL form. Cloudflare Pages serves every prerendered page
 * from `/<path>/index.html` and 301s `/<path>` to `/<path>/`, so the
 * trailing-slash form is the one that returns 200 directly. Canonicals, the
 * sitemap, schema URLs, and raw-HTML internal links all go through this so
 * none of them point at a redirect. File paths (anything with an extension,
 * such as `/downloads/x.pdf` or `/llms.txt`) are left alone, and any
 * `#hash` or `?query` suffix is preserved after the slash.
 */
export function withTrailingSlash(path: string): string {
  const m = /^([^?#]*)(.*)$/.exec(path) ?? ["", path, ""];
  let p = m[1] || "/";
  const rest = m[2] ?? "";
  if (!p.startsWith("/")) p = `/${p}`;
  if (!p.endsWith("/") && !/\.[a-z0-9]{2,5}$/i.test(p)) p = `${p}/`;
  return `${p}${rest}`;
}

export function canonical(path: string): string {
  return `${SITE.url}${withTrailingSlash(path)}`;
}

/**
 * Shrinkopedia topic-hub URL, e.g. shrinkopediaHub("sleep") →
 * https://shrinkopedia.com/hubs/sleep/. Used by the NetworkCompass and the
 * Knowledge Path HUB step. Always trailing-slash, never hardcode the path.
 */
export function shrinkopediaHub(slug: string): string {
  return `${SHRINKOPEDIA_URL}/hubs/${slug}/`;
}

/**
 * Shrinkopedia cross-network cluster URL, e.g.
 * shrinkopediaCluster("panic-disorder") →
 * https://shrinkopedia.com/clusters/panic-disorder/. Used by the §6
 * cross-network cluster line on condition pages. Always trailing-slash.
 */
export function shrinkopediaCluster(slug: string): string {
  return `${SHRINKOPEDIA_URL}/clusters/${slug}/`;
}
